import {ProductHierarchyContext} from "../PA.Utils/ProductHierarchyContext";
import * as AsyncLock from 'async-lock';
import * as _ from 'lodash';
import {Product_Hierarchy_Common_Levels} from "../PA.MD.Core/MD.Constants";
import {PeriodService} from "./PeriodService";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {IDateRange} from "../PA.Core/PA.Core.IF.Basic";

//make one call for date range, one for period WB_ID. look up effective dates for WB_ID, cache those, then call date range function

export class ProductHierarchyService {

    private static dctPrdHierContextCache: { [id: string]: ProductHierarchyContext } = {};
    private static dctLock: any = new AsyncLock();

    static getNodesAtOrgLevel(prdHierWbID: number, prdOrgLevel: string): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    context.getNodesAtOrgLevel(prdOrgLevel)
                        .then((keys) => {
                            resolve(keys);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static isPrdGrpWbIdInHierarchy(prdHierWbID: number, prdGrpWbID: number): Promise<boolean> {
        return new Promise<boolean>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    return context.isPrdGrpWbIDInHierarchy(prdGrpWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static isPrdWbIdInHierarchy(prdHierWbID: number, prdGrpWbID: number): Promise<boolean> {
        return new Promise<boolean>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    return context.isPrdWbIdInHierarchy(prdGrpWbID);
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //<editor-fold desc="Date range methods">

    static getChildren(prdHierWbID: number, prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrgLevel?: string, depthLimit?: number): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    context.getChildren(prdGrpWbID, effectiveStartDt, effectiveEndDt, prdOrgLevel, depthLimit)
                        .then((keys) => {

                            resolve(keys);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getPrdGrpParent(prdHierWbID: number, prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrgLevel?: string): Promise<number> {
        return new Promise<number>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    context.getPrdGrpParentAtOrgLevel(prdGrpWbID, effectiveStartDt, effectiveEndDt, prdOrgLevel)
                        .then((id) => {
                            resolve(id);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getAllPrdGrpParents(prdHierWbID: number, prdGrpWbID: number): Promise<{ wbID: number, dateRange: IDateRange }[]> {
        return new Promise<{ wbID: number, dateRange: IDateRange }[]>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    context.getAllPrdGrpParents(prdGrpWbID)
                        .then((results) => {
                            resolve(results);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getProductParent(prdHierWbID: number, prdWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdGrpPrntOrgLevel?: string): Promise<number> {
        return new Promise<number>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    context.getProdParentAtOrgLevel(prdWbID, effectiveStartDt, effectiveEndDt, prdGrpPrntOrgLevel)
                        .then((id) => {
                            resolve(id);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getAllProductParents(prdHierWbID: number, prdWbID: number): Promise<{ wbID: number, dateRange: IDateRange }[]> {
        return new Promise<{ wbID: number, dateRange: IDateRange }[]>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    return context.getAllProdParents(prdWbID)
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //given WB_PRDGRP_IDs, return a map of each to its planning level product
    static getPrdGrpPlanningLevels(prdHierWbID: number, prdGrpWbIDs: number[], effectiveStartDt: Date, effectiveEndDt: Date): Promise<{ [p: number]: number }> {
        return new Promise<{ [id: number]: number }>((resolve, error) => {
            let mapping: { [id: number]: number } = {};
            ProductHierarchyService.getPlanningLevelsAsync(prdHierWbID, prdGrpWbIDs, effectiveStartDt, effectiveEndDt, mapping, false)
                .then((ignore) => {
                    resolve(mapping);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //given WB_PROD_IDs, return a map of each to its planning level product
    static getProductPlanningLevels(prdHierWbID: number, prodWbIDs: number[], effectiveStartDt: Date, effectiveEndDt: Date): Promise<{ [p: number]: number }> {
        return new Promise<{ [id: number]: number }>((resolve, error) => {
            let mapping: { [id: number]: number } = {};
            ProductHierarchyService.getPlanningLevelsAsync(prdHierWbID, prodWbIDs, effectiveStartDt, effectiveEndDt, mapping, true)
                .then((ignore) => {
                    resolve(mapping);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //</editor-fold>

    //<editor-fold desc="PeriodWbID methods">

    static getChildrenByPeriodWbID(prdHierWbID: number, prdGrpWbID: number, periodWbID: number, prdOrgLevel?: string, depthLimit?: number): Promise<number[]> {
        return new Promise<number[]>((resolve, error) => {
            ProductHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange) => {

                    if (_.isUndefined(dateRange)) {
                        resolve([]);
                        return;
                    }

                    ProductHierarchyService.getChildren(prdHierWbID, prdGrpWbID, dateRange.StartDt, dateRange.EndDt, prdOrgLevel, depthLimit)
                        .then((results) => {
                            resolve(results);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getPrdGrpParentByPeriodWbID(prdHierWbID: number, prdGrpWbID: number, periodWbID: number, prdOrgLevel?: string): Promise<number> {
        return new Promise<number>((resolve, error) => {
            ProductHierarchyService.getDateRangeFromPeriodWbID(periodWbID)
                .then((dateRange) => {

                    if (_.isUndefined(dateRange)) {
                        resolve(undefined);
                        return;
                    }

                    ProductHierarchyService.getPrdGrpParent(prdHierWbID, prdGrpWbID, dateRange.StartDt, dateRange.EndDt, prdOrgLevel)
                        .then((result) => {
                            resolve(result);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getProductParentByPeriodWbID(prdHierWbID: number, prdWbID: number, periodWBID: number, prdGrpPrntOrgLevel?: string): Promise<number> {
        return new Promise<number>((resolve, error) => {
            ProductHierarchyService.getDateRangeFromPeriodWbID(periodWBID)
                .then((dateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve(undefined);
                        return;
                    }

                    ProductHierarchyService.getProductParent(prdHierWbID, prdWbID, dateRange.StartDt, dateRange.EndDt, prdGrpPrntOrgLevel)
                        .then((result) => {
                            resolve(result);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getProductParentPeriodWbIDMap(prdHierWbID: number, prdWbID: number, periodWBID: number, childPeriodTypeWbID: number, prdGrpPrntOrgLevel?: string): Promise<{ [p: number]: number }> {
        return new Promise<{ [periodWbID: number]: number }>((resolve, error) => {
            let retVal: { [periodWbID: number]: number } = {};
            ProductHierarchyService.getProductParentPeriodWbIDMapAsync(prdHierWbID, prdWbID, periodWBID, childPeriodTypeWbID, retVal)
                .then((ignore) => {
                    resolve(retVal);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getPrdGrpParentPeriodWbIDMap(prdHierWbID: number, prdGrpWbID: number, periodWBID: number, childPeriodTypeWbID: number, prdGrpPrntOrgLevel?: string): Promise<{ [p: number]: number }> {
        return new Promise<{ [periodWbID: number]: number }>((resolve, error) => {
            let retVal: { [periodWbID: number]: number } = {};
            ProductHierarchyService.getPrdGrpParentPeriodWbIDMapAsync(prdHierWbID, prdGrpWbID, periodWBID, childPeriodTypeWbID, retVal)
                .then((ignore) => {
                    resolve(retVal);
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getPrdGrpPlanningLevelsByPeriodWbID(prdHierWbID: number, prdGrpWbIDs: number[], periodWBID: number): Promise<{ [p: number]: number }> {
        return new Promise<{ [id: number]: number }>((resolve, error) => {
            ProductHierarchyService.getDateRangeFromPeriodWbID(periodWBID)
                .then((dateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve({});
                        return;
                    }

                    ProductHierarchyService.getPrdGrpPlanningLevels(prdHierWbID, prdGrpWbIDs, dateRange.StartDt, dateRange.EndDt)
                        .then((results) => {
                            resolve(results);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getProductPlanningLevelsByPeriodWbID(prdHierWbID: number, prodWbIDs: number[], periodWBID: number): Promise<{ [p: number]: number }> {
        return new Promise<{ [id: number]: number }>((resolve, error) => {
            ProductHierarchyService.getDateRangeFromPeriodWbID(periodWBID)
                .then((dateRange) => {
                    if (_.isUndefined(dateRange)) {
                        resolve({});
                        return;
                    }

                    ProductHierarchyService.getProductPlanningLevels(prdHierWbID, prodWbIDs, dateRange.StartDt, dateRange.EndDt)
                        .then((results) => {
                            resolve(results);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //</editor-fold>

    static resetCache(): string {
        let retVal: string = "";
        retVal = "Cleared " + _.values(ProductHierarchyService.dctPrdHierContextCache).length + " hierarchies";
        ProductHierarchyService.dctPrdHierContextCache = {};
        return retVal;
    }

    static resetCacheByHierarchy(prdHierWbID: number): Promise<void> {
        return new Promise<void>((resolve, error) => {
            ProductHierarchyService.getHierarchyContext(prdHierWbID)
                .then((context) => {
                    return context.reset();
                })
                .then((ignore) => {
                    resolve();
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //<editor-fold desc="Private methods">

    private static async getProductParentPeriodWbIDMapAsync(prdHierWbID: number, prdWbID: number, periodWBID: number, childPeriodTypeWbID: number, retVal: { [p: number]: number }, prdGrpPrntOrgLevel?: string) {

        let periods = await PeriodService.getAllChildrenByWBIDChildTypeWbId(childPeriodTypeWbID, periodWBID);

        if (_.isArray(periods) && !_.isEmpty(periods)) {
            for (let aPeriod of periods) {
                let prdGrpPrntWbID = await ProductHierarchyService.getProductParentByPeriodWbID(prdHierWbID, prdWbID, aPeriod.cif.WB_PERIOD_ID, prdGrpPrntOrgLevel);
                retVal[aPeriod.cif.WB_PERIOD_ID] = prdGrpPrntWbID;
            }
        }
    }

    private static async getPrdGrpParentPeriodWbIDMapAsync(prdHierWbID: number, prdGrpWbID: number, periodWBID: number, childPeriodTypeWbID: number, retVal: { [p: number]: number }, prdGrpPrntOrgLevel?: string) {

        let periods = await PeriodService.getAllChildrenByWBIDChildTypeWbId(childPeriodTypeWbID, periodWBID);

        if (_.isArray(periods) && !_.isEmpty(periods)) {
            for (let aPeriod of periods) {
                let prdGrpPrntWbID = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(prdHierWbID, prdGrpWbID, aPeriod.cif.WB_PERIOD_ID, prdGrpPrntOrgLevel);
                retVal[aPeriod.cif.WB_PERIOD_ID] = prdGrpPrntWbID;
            }
        }
    }

    //use an async call since we have to wait on promises during a loop
    private static async getPlanningLevelsAsync(prdHierWbID: number, prdWbIDsCSV: number[], effectiveStartDt: Date, effectiveEndDt: Date, retVal: { [p: number]: number }, bIsFnsPrd: boolean) {
        let planningLevelID: number;
        for (let anID of prdWbIDsCSV) {
            if (bIsFnsPrd) {
                planningLevelID = await ProductHierarchyService.getProductParent(prdHierWbID, anID, effectiveStartDt, effectiveEndDt, Product_Hierarchy_Common_Levels.PlanLevel);
            } else {
                planningLevelID = await ProductHierarchyService.getPrdGrpParent(prdHierWbID, anID, effectiveStartDt, effectiveEndDt, Product_Hierarchy_Common_Levels.PlanLevel);
            }
            retVal[anID] = planningLevelID;
        }
    }

    private static getDateRangeFromPeriodWbID(periodWbID: number): Promise<IDateRange> {
        return new Promise<IDateRange>((resolve, error) => {
            PeriodService.getPeriodByWBID(periodWbID)
                .then((result) => {
                    if (!_.isUndefined(result)) {
                        resolve({
                            StartDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_START_DT),
                            EndDt: DateUtils.convertFromSAPBW(result.cif.PERIOD_END_DT)
                        });
                    } else {
                        resolve(undefined);
                    }
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    private static getHierarchyContext(prdHierWbID: number): Promise<ProductHierarchyContext> {
        return new Promise<ProductHierarchyContext>((resolve, error) => {

            ProductHierarchyService.dctLock.acquire('global',
                (done: any) => {

                    let context: ProductHierarchyContext;

                    if (prdHierWbID in ProductHierarchyService.dctPrdHierContextCache) {
                        context = ProductHierarchyService.dctPrdHierContextCache[prdHierWbID];
                    } else {
                        context = new ProductHierarchyContext(prdHierWbID);
                        ProductHierarchyService.dctPrdHierContextCache[prdHierWbID] = context;
                    }

                    done(null, ProductHierarchyService.dctPrdHierContextCache[prdHierWbID]);
                    resolve(context);
                })

        })
    }

    //</editor-fold>

}
